import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Mic, Square, Loader2, Sparkles, Home as HomeIcon, ArrowRight, Star, Search, Send, Check,
  Handshake, RefreshCw, MapPin, Wallet, Building2, Pencil,
} from "lucide-react";
import { API, fmtPrice } from "./api";

const GOLD = "var(--sp, #C19A6B)";
// Doré/accent avec transparence — suit la couleur de marque (SAFTI orange…) via var(--sp).
const GOLDA = (p) => `color-mix(in srgb, var(--sp, #C19A6B) ${p}, transparent)`;

// Onde vocale dorée premium — grande, élégante, animée pendant l'enregistrement.
export function GoldWave({ active }) {
  const bars = 28;
  return (
    <div className="flex items-center justify-center gap-[3px] h-16" aria-hidden="true" data-testid="ea-goldwave">
      {Array.from({ length: bars }).map((_, i) => {
        const mid = Math.abs(i - bars / 2);
        const base = 10 + (bars / 2 - mid) * 2.2;
        return (
          <motion.span
            key={i}
            style={{ width: 3, borderRadius: 5, background: `linear-gradient(180deg, #E7C9A0, ${GOLD})` }}
            animate={{ height: active ? [base * 0.4, base * 1.6, base * 0.4] : [base * 0.5, base * 0.95, base * 0.5] }}
            transition={{ duration: (active ? 0.9 : 1.7) + (i % 5) * 0.08, repeat: Infinity, ease: "easeInOut", delay: (i % 6) * 0.05 }}
          />
        );
      })}
    </div>
  );
}

// Petites étoiles dorées discrètes.
function Stars() {
  const pts = [
    { top: "8%", left: "6%", s: 10, d: 0 }, { top: "16%", right: "10%", s: 8, d: 0.6 },
    { bottom: "14%", left: "12%", s: 7, d: 1.1 }, { bottom: "22%", right: "8%", s: 11, d: 0.3 },
    { top: "40%", left: "3%", s: 6, d: 0.9 }, { top: "30%", right: "4%", s: 6, d: 1.4 },
  ];
  return (
    <>
      {pts.map((p, i) => (
        <motion.span key={i} className="absolute pointer-events-none" style={{ ...p, color: GOLD }}
          animate={{ opacity: [0.15, 0.7, 0.15], scale: [0.85, 1.1, 0.85] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: p.d }}>
          <Star style={{ width: p.s, height: p.s }} fill={GOLD} strokeWidth={0} />
        </motion.span>
      ))}
    </>
  );
}

function ScoreBadge({ score }) {
  return (
    <span data-testid="ea-score-badge" className="inline-flex items-center gap-1 text-[11px] font-bold text-white px-2 py-1 rounded-[5px]"
      style={{ background: `linear-gradient(135deg, #D4B283, ${GOLD})`, boxShadow: "0 4px 12px rgba(193,154,107,0.4)" }}>
      <Sparkles className="w-3 h-3" /> {score}%
    </span>
  );
}

function RelationForm({ slug, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/listings/${slug}/mise-en-relation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...f, kind: "acquereur" }),
      });
      setDone(true); onDone && onDone();
    } catch (_) {}
    setBusy(false);
  };
  if (done) return <p className="text-xs text-emerald-600 flex items-center gap-1.5 mt-2" data-testid="ea-relation-done"><Check className="w-4 h-4" /> Demande envoyée. Le professionnel vous recontactera.</p>;
  return (
    <div className="mt-2 grid gap-2" data-testid="ea-relation-form">
      <div className="grid grid-cols-2 gap-2">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nom" className="border border-[#E5E7EB] rounded-[5px] px-2.5 py-1.5 text-xs outline-none focus:border-[#C19A6B]" data-testid="ea-relation-name" />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Téléphone" className="border border-[#E5E7EB] rounded-[5px] px-2.5 py-1.5 text-xs outline-none focus:border-[#C19A6B]" data-testid="ea-relation-phone" />
      </div>
      <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-mail" className="border border-[#E5E7EB] rounded-[5px] px-2.5 py-1.5 text-xs outline-none focus:border-[#C19A6B]" data-testid="ea-relation-email" />
      <button onClick={submit} disabled={busy || (!f.name && !f.email && !f.phone)} data-testid="ea-relation-submit"
        className="inline-flex items-center justify-center gap-1.5 text-white text-xs font-semibold uppercase tracking-wider rounded-[5px] py-2 disabled:opacity-50" style={{ background: GOLD }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer ma demande
      </button>
    </div>
  );
}

// Composant Espace Acquéreur — recherche vocale premium, réutilisé sur tout le site.
// layout="hero" => refonte immersive (3 panneaux desktop / parcours guidé mobile).
export default function EspaceAcquereur({
  slug = "", brand = "OFF MARKETTE", compact = false, intro = "", value, onChange,
  layout = "", accountPanel = null, accountCta = null, connected = false,
}) {
  const { t } = useTranslation();
  const hero = layout === "hero";
  const [innerText, setInnerText] = useState("");
  const text = value !== undefined ? value : innerText;
  const setText = (v) => { const nv = typeof v === "function" ? v(text) : v; if (onChange) onChange(nv); else setInnerText(nv); };
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null); // { matches, saved, criteria }
  const [relFor, setRelFor] = useState(null);
  const [lastQuery, setLastQuery] = useState("");
  const [emailSel, setEmailSel] = useState({ email: "", name: "" });
  const [emailState, setEmailState] = useState("idle");
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [step, setStep] = useState(1); // parcours mobile (hero)
  const [localErr, setLocalErr] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const resultsRef = useRef(null);

  const genSummary = async () => {
    const q = (text || "").trim();
    if (q.length < 8 || summarizing) return false;
    setSummarizing(true);
    let ok = false;
    try {
      const res = await fetch(`${API}/acquereurs/summary`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ raw_text: q }),
      });
      const d = await res.json();
      setSummary(d); ok = res.ok;
    } catch (_) { setSummary(null); }
    setSummarizing(false);
    return ok;
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t2) => t2.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", new Blob(chunksRef.current, { type: "audio/webm" }), "recherche.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd });
          const d = await res.json();
          const spoken = (d.text || "").trim();
          setText((tt) => (tt ? tt + " " : "") + spoken);
          setTranscribing(false);
          if (spoken) doSearch((text ? text + " " : "") + spoken);
        } catch (_) { setTranscribing(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) { /* micro indisponible */ }
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  const doSearch = async (raw) => {
    const q = (raw ?? text).trim();
    if (q.length < 8 || searching) return;
    setSearching(true); setResult(null); setRelFor(null); setLastQuery(q); setEmailState("idle");
    try {
      const res = await fetch(`${API}/acquereurs/search`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ raw_text: q, slug }),
      });
      const d = await res.json();
      setResult(d);
    } catch (_) {
      setResult({ matches: [], saved: false, error: true });
    }
    setSearching(false);
  };

  // Défilement doux vers les résultats une fois la recherche terminée (desktop + mobile).
  useEffect(() => {
    if (hero && result) {
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 160);
    }
  }, [result, hero]);

  const heroAnalyze = async () => {
    if ((text || "").trim().length < 8) { setLocalErr(t("acqspace.err_describe")); return; }
    setLocalErr("");
    await genSummary();
    setStep(2);
  };

  const sendEmailSelection = async () => {
    if (!emailSel.email || emailState === "sending") return;
    setEmailState("sending");
    try {
      const res = await fetch(`${API}/acquereurs/email-selection`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: emailSel.email, name: emailSel.name, raw_text: lastQuery }),
      });
      const d = await res.json();
      setEmailState(d.sent ? "sent" : "error");
    } catch (_) { setEmailState("error"); }
  };

  // ============ Blocs de résultats (partagés) ============
  const ResultsBlock = (
    <AnimatePresence>
      {result && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="ea-results">
          {result.matches && result.matches.length > 0 ? (
            <>
              <p className={`calli text-2xl sm:text-3xl mb-4 flex items-center gap-2 ${hero ? "text-white" : ""}`} style={hero ? {} : { color: GOLD }} data-testid="ea-results-title">
                <Sparkles className="w-5 h-5" style={{ color: GOLD }} />
                {t(result.matches.length > 1 ? "acqspace.match_other" : "acqspace.match_one", { count: result.matches.length })}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.matches.map((l, i) => (
                  <motion.div key={l.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="bg-white border border-[#E5E7EB] rounded-[5px] overflow-hidden flex flex-col" data-testid={`ea-match-${l.slug}`}
                    style={{ boxShadow: "0 8px 24px -12px rgba(17,24,39,0.15)" }}>
                    <a href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" className="relative block">
                      <div className="aspect-[4/3] bg-[#F3F4F6]">
                        {l.cover || (l.images && l.images[0])
                          ? <img src={l.cover || l.images[0]} alt={l.title} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center"><HomeIcon className="w-8 h-8 text-[#D1D5DB]" /></div>}
                      </div>
                      <div className="absolute top-2.5 right-2.5"><ScoreBadge score={l.match_score} /></div>
                    </a>
                    <div className="p-3 flex flex-col gap-1 flex-1">
                      <p className="text-sm font-semibold text-[#111827] line-clamp-1">{l.title}</p>
                      <p className="text-xs text-[#6B7280] line-clamp-1">{[l.city, l.surface ? `${l.surface} m²` : null].filter(Boolean).join(" · ")}</p>
                      {l.price ? <p className="text-sm font-bold" style={{ color: GOLD }}>{fmtPrice(l.price)}</p> : null}
                      <p className="text-[11px] font-semibold mt-1.5 leading-snug flex items-center gap-1.5" style={{ color: GOLD }} data-testid={`ea-match-why-${l.slug}`}>
                        <Sparkles className="w-3 h-3" /> {t("acqspace.why_title")}
                        <span className="text-[#6B7280] font-normal">· {t("acqspace.match_pct", { pct: l.match_score })}</span>
                      </p>
                      {(l.match_reasons || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`ea-match-reasons-${l.slug}`}>
                          {l.match_reasons.map((r, ri) => (
                            <span key={ri} className="inline-flex items-center gap-1 text-[10px] text-[#A88358] border border-[#C19A6B]/30 rounded-full px-2 py-0.5">
                              <Check className="w-3 h-3" /> {r}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2">
                        {l.is_apport ? (
                          relFor === l.slug ? <RelationForm slug={l.slug} onDone={() => {}} /> : (
                            <button onClick={() => setRelFor(l.slug)} data-testid={`ea-relation-open-${l.slug}`}
                              className="w-full inline-flex items-center justify-center gap-1.5 text-white text-xs font-semibold uppercase tracking-wider rounded-[5px] py-2" style={{ background: GOLD }}>
                              <Handshake className="w-4 h-4" /> {t("acqspace.get_in_touch")}
                            </button>
                          )
                        ) : (
                          <a href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`ea-contact-${l.slug}`}
                            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider rounded-[5px] py-2 border border-[#C19A6B] text-[#A88358] hover:bg-[#C19A6B] hover:text-white transition-colors">
                            {t("acqspace.see_property")} <ArrowRight className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-5 bg-white border border-[#E5E7EB] rounded-[5px] p-4" data-testid="ea-email-selection">
                {emailState === "sent" ? (
                  <p className="text-sm text-emerald-600 flex items-center gap-2" data-testid="ea-email-sent"><Check className="w-4 h-4" /> {t("acqspace.email_sent")}</p>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#111827] whitespace-nowrap">
                      <Send className="w-4 h-4" style={{ color: GOLD }} /> {t("acqspace.email_selection_label")}
                    </div>
                    <input value={emailSel.name} onChange={(e) => setEmailSel({ ...emailSel, name: e.target.value })} placeholder={t("acqspace.fn")} data-testid="ea-email-name" autoComplete="given-name"
                      className="sm:w-32 border border-[#E5E7EB] rounded-[5px] px-3 py-2 text-sm outline-none focus:border-[#C19A6B]" />
                    <input value={emailSel.email} onChange={(e) => setEmailSel({ ...emailSel, email: e.target.value })} type="email" inputMode="email" autoComplete="email" placeholder={t("acqspace.email")} data-testid="ea-email-input"
                      className="flex-1 border border-[#E5E7EB] rounded-[5px] px-3 py-2 text-sm outline-none focus:border-[#C19A6B]" />
                    <button onClick={sendEmailSelection} disabled={!emailSel.email || emailState === "sending"} data-testid="ea-email-submit"
                      className="inline-flex items-center justify-center gap-1.5 text-white text-xs font-bold uppercase tracking-wider rounded-[5px] px-4 py-2 whitespace-nowrap disabled:opacity-50" style={{ background: GOLD }}>
                      {emailState === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : t("acqspace.email_send")}
                    </button>
                  </div>
                )}
                {emailState === "error" && <p className="text-xs text-red-500 mt-2">{t("acqspace.email_error")}</p>}
              </div>
            </>
          ) : (
            <div className="bg-white border border-[#E5E7EB] rounded-[5px] p-6 sm:p-8 relative overflow-hidden" data-testid="ea-no-result">
              <Stars />
              <div className="relative max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 flex items-center gap-1.5" style={{ color: GOLD }}>
                  <Sparkles className="w-3.5 h-3.5" /> {t("acqspace.no_result_title")}
                </p>
                <p className="text-sm text-[#4B5563] mt-1 leading-relaxed">{t("acqspace.no_result_text")}</p>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ============ LAYOUT HERO (refonte immersive) ============
  if (hero) {
    const gold = "#C19A6B";
    const panelCls = "relative rounded-2xl border border-white/[0.14] bg-[#0e0e10]/60 backdrop-blur-[12px] shadow-[0_24px_70px_-30px_rgba(0,0,0,0.85)] p-4 sm:p-5 flex flex-col";
    const goldBtn = "inline-flex items-center justify-center gap-2 rounded-full text-[#050505] font-semibold uppercase tracking-wider text-xs px-6 py-3.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed";
    const goldBtnStyle = { background: gold };
    const crit = summary ? [
      { icon: MapPin, label: t("acqspace.crit_sector"), value: (summary.secteurs || []).join(", ") },
      { icon: Wallet, label: t("acqspace.crit_budget"), value: summary.budget },
      { icon: Building2, label: t("acqspace.crit_type"), value: (summary.type_bien || []).join(", ") },
    ].filter((c) => c.value) : [];
    const wishes = summary ? (summary.criteres || []) : [];
    const steps = [t("acqspace.mobile_step1"), t("acqspace.mobile_step2"), t("acqspace.mobile_step3")];

    // CTA sticky mobile (change selon l'étape)
    let sticky = null;
    if (step === 1) sticky = { label: t("acqspace.analyze_cta"), on: heroAnalyze, busy: summarizing, disabled: (text || "").trim().length < 8, testid: "ea-sticky-analyze" };
    else if (step === 2) sticky = result
      ? { label: accountCta?.label, on: () => setStep(3), busy: false, disabled: false, testid: "ea-sticky-tostep3" }
      : { label: t("acqspace.find_cta"), on: () => doSearch(), busy: searching, disabled: (text || "").trim().length < 8, testid: "ea-sticky-find" };
    else sticky = { label: accountCta?.label, on: accountCta?.onSubmit, busy: accountCta?.busy, disabled: accountCta?.disabled, testid: "ea-sticky-account" };

    // Sous-bloc 1 — Décrivez votre recherche (bloc gauche fusionné)
    const SearchInner = (
      <div className={`${step === 1 ? "block" : "hidden"} lg:flex lg:flex-col lg:h-full`} data-testid="ea-panel-search">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-[#050505]" style={{ background: gold }}>1</span>
            <div>
              <h3 className="text-base md:text-lg font-medium text-white leading-tight">{t("acqspace.step1_title")}</h3>
            </div>
          </div>
          <textarea value={text} onChange={(e) => { setText(e.target.value); setSummary(null); setLocalErr(""); }}
            placeholder={t("acqspace.textarea_ph")} data-testid="ea-text-input" aria-label={t("acqspace.step1_title")}
            className="w-full min-h-[92px] flex-1 bg-white/[0.06] border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#C19A6B] resize-none transition-colors" />

          <div className="mt-3 flex flex-col items-center">
            <div className="w-full"><GoldWave active={recording} /></div>
            <div className="-mt-2 flex flex-col items-center gap-1.5">
              {!recording ? (
                <motion.button onClick={startRec} disabled={transcribing || searching} data-testid="ea-mic-start" aria-label={t("acqspace.voice_btn")}
                  animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} whileHover={{ scale: 1.06 }}
                  className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-wider rounded-full px-6 py-3 disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg, #D4B283, ${gold})`, boxShadow: "0 0 24px rgba(193,154,107,0.5)" }}>
                  {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                  {transcribing ? t("acqspace.transcribing") : t("acqspace.voice_btn")}
                </motion.button>
              ) : (
                <button onClick={stopRec} data-testid="ea-mic-stop"
                  className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-wider rounded-full px-6 py-3"
                  style={{ background: gold, boxShadow: "0 0 24px rgba(193,154,107,0.5)" }}>
                  <Square className="w-4 h-4" /> {t("acqspace.listening")}
                </button>
              )}
            </div>
          </div>

          {localErr && <p className="text-xs text-red-400 mt-3 text-center" data-testid="ea-local-err">{localErr}</p>}

          <button onClick={heroAnalyze} disabled={summarizing || (text || "").trim().length < 8} data-testid="ea-analyze-btn"
            className={`${goldBtn} hidden lg:inline-flex mt-4 w-full hover:brightness-110`} style={goldBtnStyle}>
            {summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("acqspace.analyze_cta")}
          </button>
      </div>
    );

    // Sous-bloc 2 — Ce que l'IA a compris (bloc gauche fusionné)
    const ProfileInner = (
      <div className={`${step === 2 ? "block" : "hidden"} lg:flex lg:flex-col lg:h-full lg:border-l lg:border-white/10 lg:pl-6`} data-testid="ea-panel-profile">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-[#050505]" style={{ background: gold }}>2</span>
            <h3 className="text-base md:text-lg font-medium text-white leading-tight">{t("acqspace.step2_title")}</h3>
          </div>

          <div className="flex-1">
            {searching ? (
              <MatchingAnim t={t} gold={gold} />
            ) : summary ? (
              <div data-testid="ea-criteria">
                {summary.summary && (
                  <p className="text-sm text-zinc-300 italic leading-relaxed border-l-2 pl-3 mb-4" style={{ borderColor: gold }}>« {summary.summary} »</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {crit.map((c, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5" data-testid={`ea-crit-${i}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest" style={{ color: gold }}>
                        <c.icon className="w-3.5 h-3.5" /> {c.label}
                      </div>
                      <p className="text-sm text-white mt-1 leading-snug">{c.value}</p>
                    </div>
                  ))}
                </div>
                {wishes.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-1.5" style={{ color: gold }}>
                      <Sparkles className="w-3.5 h-3.5" /> {t("acqspace.crit_wishes")}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {wishes.map((w, i) => <span key={i} className="text-[11px] text-zinc-200 border border-white/15 rounded-full px-2.5 py-0.5">{w}</span>)}
                    </div>
                  </div>
                )}
                <button onClick={() => setStep(1)} data-testid="ea-edit-btn" className="lg:hidden mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> {t("acqspace.edit")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-start justify-center h-full py-3" data-testid="ea-empty">
                <div className="w-11 h-11 rounded-full flex items-center justify-center mb-3" style={{ background: GOLDA("14%") }}>
                  <Sparkles className="w-5 h-5" style={{ color: gold }} />
                </div>
                <p className="text-sm text-white font-medium">{t("acqspace.empty_title")}</p>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{t("acqspace.empty_text")}</p>
              </div>
            )}
          </div>

          <button onClick={() => doSearch()} disabled={searching || (text || "").trim().length < 8} data-testid="ea-search-btn"
            className={`${goldBtn} hidden lg:inline-flex mt-4 w-full hover:brightness-110`} style={goldBtnStyle}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {t("acqspace.find_cta")}
          </button>
      </div>
    );

    // Bloc gauche fusionné (2× la largeur du bloc de droite) — recherche + profil IA côte à côte.
    const MergedLeft = (
      <div className={`${step === 1 || step === 2 ? "block" : "hidden"} lg:block`} data-testid="ea-merged-left">
        <div className={`${panelCls} h-full`}>
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6 flex-1">
            {SearchInner}
            {ProfileInner}
          </div>
        </div>
      </div>
    );

    // Panneau 3 — Mon espace (fourni par le parent : auth / recherches)
    const Panel3 = (
      <div className={`${step === 3 ? "block" : "hidden"} lg:block`} data-testid="ea-panel-account">
        <div className={panelCls}>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-[#050505]" style={{ background: gold }}>3</span>
            <h3 className="text-base md:text-lg font-medium text-white leading-tight">{t("acqspace.step3_title")}</h3>
          </div>
          <div className="flex-1">{accountPanel}</div>
          {accountCta && (
            <button onClick={accountCta.onSubmit} disabled={accountCta.busy || accountCta.disabled} data-testid="ea-account-cta"
              className={`${goldBtn} hidden lg:inline-flex mt-4 w-full hover:brightness-110`} style={goldBtnStyle}>
              {accountCta.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {accountCta.label}
            </button>
          )}
        </div>
      </div>
    );

    return (
      <div data-testid="espace-acquereur" className="w-full">
        {/* HERO immersif */}
        <section className="relative overflow-hidden bg-[#050505] min-h-screen lg:min-h-[100svh] flex items-center" data-testid="ea-hero">
          <img
            src="/assets/espace-acq-desktop.webp"
            srcSet="/assets/espace-acq-mobile.webp 1000w, /assets/espace-acq-tablet.webp 1200w, /assets/espace-acq-desktop.webp 1672w"
            sizes="100vw" alt="" aria-hidden="true" fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.62) 0%, rgba(5,5,5,0.34) 26%, rgba(5,5,5,0.42) 62%, rgba(5,5,5,0.72) 100%)" }} />
          <div className="relative z-10 w-full mx-auto px-5 sm:px-6 pt-28 sm:pt-32 pb-28 lg:pb-14" style={{ maxWidth: "min(90vw, 1200px)" }}>
            {/* En-tête */}
            <div className="max-w-3xl">
              <p className="text-[11px] uppercase tracking-[0.32em] font-semibold" style={{ color: gold }} data-testid="ea-eyebrow">{t("acqspace.eyebrow")}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-[3.1rem] font-light text-white tracking-tight leading-[1.06] mt-2" data-testid="ea-hero-title">{t("acqspace.hero_title")}</h1>
              <p className="hidden sm:block text-base text-zinc-300 font-light mt-3 max-w-2xl leading-relaxed">{t("acqspace.hero_sub")}</p>
              <p className="text-sm text-zinc-400 mt-2">{t("acqspace.hero_hint")}</p>
            </div>

            {/* Indicateur d'étape (mobile) */}
            <div className="lg:hidden mt-6 flex items-center gap-2" data-testid="ea-stepper">
              {steps.map((label, i) => {
                const n = i + 1; const active = step === n; const doneStep = step > n;
                return (
                  <React.Fragment key={n}>
                    <button onClick={() => setStep(n)} className="flex items-center gap-2 min-w-0" data-testid={`ea-step-${n}`}>
                      <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${active ? "text-[#050505]" : doneStep ? "text-[#050505]" : "text-zinc-400 border border-white/25"}`}
                        style={active || doneStep ? { background: gold } : {}}>
                        {doneStep ? <Check className="w-3.5 h-3.5" /> : n}
                      </span>
                      <span className={`text-[11px] uppercase tracking-wider whitespace-nowrap ${active ? "inline text-white" : "hidden sm:inline text-zinc-500"}`}>{label}</span>
                    </button>
                    {n < 3 && <span className="flex-1 h-px bg-white/15" />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Grille 3 colonnes (desktop) / étape active (mobile) */}
            <div className="mt-6 grid gap-4 lg:gap-5 lg:grid-cols-[2fr_1fr] items-stretch" data-testid="ea-grid">
              {MergedLeft}
              {Panel3}
            </div>
          </div>

          {/* CTA sticky mobile */}
          {sticky && sticky.label && (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-6 bg-gradient-to-t from-[#050505] via-[#050505]/92 to-transparent" data-testid="ea-sticky-bar">
              <button onClick={sticky.on} disabled={sticky.busy || sticky.disabled} data-testid={sticky.testid}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full text-[#050505] font-semibold uppercase tracking-wider text-sm py-4 disabled:opacity-50 shadow-[0_8px_30px_rgba(193,154,107,0.4)]" style={{ background: gold }}>
                {sticky.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {sticky.label}
              </button>
            </div>
          )}
        </section>

        {/* Résultats + matching (section sombre sous le hero) */}
        {(result || searching) && (
          <section ref={resultsRef} className="bg-[#050505] px-5 sm:px-6 py-10 scroll-mt-24" data-testid="ea-results-section">
            <div className="mx-auto" style={{ maxWidth: "min(90vw, 1200px)" }}>{ResultsBlock}</div>
          </section>
        )}
      </div>
    );
  }

  // ============ LAYOUT PAR DÉFAUT (conservé pour les autres pages) ============
  return (
    <div data-testid="espace-acquereur" className="w-full">
      <div className="relative overflow-hidden bg-white border border-[#E5E7EB] rounded-[5px] p-6 sm:p-8"
        style={{ boxShadow: "0 20px 60px -20px rgba(17,24,39,0.18)" }}>
        <Stars />
        <div className="relative grid lg:grid-cols-3 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-2 text-center flex flex-col justify-center">
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold mb-2" style={{ color: GOLD }}>Espace Acquéreur</p>
            <h3 className="calli text-2xl sm:text-[1.7rem] lg:text-3xl leading-tight" style={{ color: GOLD }}>Décrivez le bien de vos rêves</h3>
            <p className="text-base sm:text-lg text-[#4B5563] mt-3 max-w-xl mx-auto leading-relaxed">Parlez librement : budget, secteur, type de bien, chambres, extérieur… Notre IA trouve les meilleures opportunités de {brand}.</p>
            <div className="mt-6"><GoldWave active={recording} /></div>
            <div className="mt-5 flex flex-col items-center gap-3">
              {!recording ? (
                <motion.button onClick={startRec} disabled={transcribing || searching} data-testid="ea-mic-start"
                  animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} whileHover={{ scale: 1.06 }}
                  className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-wider rounded-[5px] px-5 py-2.5 disabled:opacity-60"
                  style={{ background: GOLD, boxShadow: "0 0 22px rgba(193,154,107,0.5)" }}>
                  {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                  {transcribing ? "Transcription…" : "Parler à l'assistant"}
                </motion.button>
              ) : (
                <button onClick={stopRec} data-testid="ea-mic-stop"
                  className="inline-flex items-center gap-2 text-white text-xs font-bold uppercase tracking-wider rounded-[5px] px-5 py-2.5"
                  style={{ background: GOLD, boxShadow: "0 0 22px rgba(193,154,107,0.5)" }}>
                  <Square className="w-4 h-4" /> Terminer
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 bg-[#F4F5F7] rounded-[5px] p-5 flex flex-col" data-testid="ea-recap-panel">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2 flex items-center gap-1.5" style={{ color: GOLD }}>
              <Sparkles className="w-3.5 h-3.5" /> Votre recherche
            </p>
            <textarea value={text} onChange={(e) => { setText(e.target.value); setSummary(null); }}
              placeholder="Expliquez votre recherche ici… ou dictez-la au micro." data-testid="ea-text-input"
              className="flex-1 min-h-[120px] bg-white border border-[#E5E7EB] rounded-[5px] px-4 py-3 text-sm text-[#111827] outline-none focus:border-[#C19A6B] resize-none" />
            {summary && summary.summary && (
              <div className="mt-3 border-l-2 pl-3 text-left" style={{ borderColor: GOLD }} data-testid="ea-summary">
                <p className="text-xs text-[#4B5563] italic leading-relaxed">« {summary.summary} »</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {summary.budget && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-[4px]" style={{ color: GOLD, border: `1px solid ${GOLDA("33%")}` }}>{summary.budget}</span>}
                  {(summary.secteurs || []).map((x, i) => <span key={`s${i}`} className="text-[10px] text-[#6B7280] border border-[#E5E7EB] px-2 py-0.5 rounded-[4px]">{x}</span>)}
                  {(summary.type_bien || []).map((x, i) => <span key={`t${i}`} className="text-[10px] text-[#6B7280] border border-[#E5E7EB] px-2 py-0.5 rounded-[4px]">{x}</span>)}
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-col gap-2">
              <button onClick={genSummary} disabled={summarizing || (text || "").trim().length < 8} data-testid="ea-regen-btn"
                className="inline-flex items-center justify-center gap-2 rounded-[5px] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border transition-colors disabled:opacity-50"
                style={{ borderColor: GOLDA("50%"), color: GOLD }}>
                {summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {summary ? "Régénérer le résumé" : "Générer le résumé"}
              </button>
              <button onClick={() => doSearch()} disabled={searching || (text || "").trim().length < 8} data-testid="ea-search-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-[5px] px-3 py-2.5 text-white text-[11px] font-bold uppercase tracking-wide whitespace-nowrap disabled:opacity-50" style={{ background: GOLD }}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Trouve mon bien idéal !
              </button>
            </div>
          </div>
        </div>
      </div>
      {ResultsBlock}
    </div>
  );
}

// Animation courte de matching IA (hero, pendant la recherche).
function MatchingAnim({ t, gold }) {
  const steps = [t("acqspace.matching_1"), t("acqspace.matching_2"), t("acqspace.matching_3"), t("acqspace.matching_4")];
  return (
    <div className="py-4" data-testid="ea-matching">
      <p className="text-sm font-medium text-white flex items-center gap-2 mb-4">
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: gold }} /> {t("acqspace.matching_title")}
      </p>
      <ul className="space-y-3">
        {steps.map((s, i) => (
          <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.5 }}
            className="flex items-center gap-2.5 text-sm text-zinc-300">
            {i < 3 ? (
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.5 + 0.35, type: "spring", stiffness: 300 }}
                className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: gold }}>
                <Check className="w-3 h-3 text-[#050505]" />
              </motion.span>
            ) : (
              <span className="w-5 h-5 rounded-full flex items-center justify-center border border-white/25">
                <Loader2 className="w-3 h-3 animate-spin" style={{ color: gold }} />
              </span>
            )}
            {s}
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
